import React, { useState, useEffect } from 'react';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, Switch, Alert, ActivityIndicator,
  KeyboardAvoidingView, Platform, ScrollView,
} from 'react-native';
import MapView, { Marker, Region } from 'react-native-maps';
import * as Location from 'expo-location';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { MapStackParamList } from '../../navigation/AppNavigator';
import { landmarksApi } from '../../api';
import { useLandmarksStore } from '../../store';

type Props = NativeStackScreenProps<MapStackParamList, 'AddLandmark'>;

export default function AddLandmarkScreen({ route, navigation }: Props) {
  const { lat: initLat, lng: initLng } = route.params ?? {};
  const [lat, setLat] = useState(initLat ?? 37.7749);
  const [lng, setLng] = useState(initLng ?? -122.4194);
  const [locationReady, setLocationReady] = useState(!!initLat);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const upsertLandmark = useLandmarksStore((s) => s.upsertLandmark);

  // If no coords were passed in (not navigated from map), snap to current GPS position
  useEffect(() => {
    if (initLat !== undefined) return;
    Location.requestForegroundPermissionsAsync().then(({ status }) => {
      if (status !== 'granted') { setLocationReady(true); return; }
      return Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
    }).then((pos) => {
      if (pos) {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
      }
      setLocationReady(true);
    }).catch(() => setLocationReady(true));
  }, []);

  function handleMapPress(e: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) {
    setLat(e.nativeEvent.coordinate.latitude);
    setLng(e.nativeEvent.coordinate.longitude);
  }

  function handleMarkerDragEnd(e: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) {
    setLat(e.nativeEvent.coordinate.latitude);
    setLng(e.nativeEvent.coordinate.longitude);
  }

  async function handleSave() {
    if (!name.trim()) {
      Alert.alert('Error', 'Please enter a name for this landmark.');
      return;
    }
    setLoading(true);
    try {
      const created = await landmarksApi.create({ name: name.trim(), lat, lng, isPublic, description });
      upsertLandmark(created);
      navigation.goBack();
    } catch (e: any) {
      Alert.alert('Failed to save', e.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  const initialRegion: Region = {
    latitude: lat,
    longitude: lng,
    latitudeDelta: 0.005,
    longitudeDelta: 0.005,
  };

  if (!locationReady) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color="#1a73e8" />
        <Text style={{ marginTop: 12, color: '#666' }}>Getting your location…</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'android' ? 80 : 0}
    >
      <View style={styles.mapContainer}>
        <MapView
          style={styles.map}
          initialRegion={initialRegion}
          onPress={handleMapPress}
        >
          <Marker
            coordinate={{ latitude: lat, longitude: lng }}
            pinColor="#1a73e8"
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        </MapView>
        <View style={styles.mapHint} pointerEvents="none">
          <Text style={styles.mapHintText}>Tap map or drag pin to position</Text>
        </View>
      </View>

      <ScrollView
        style={styles.form}
        contentContainerStyle={styles.formContent}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.coords}>📍 {lat.toFixed(6)}, {lng.toFixed(6)}</Text>

        <Text style={styles.label}>Landmark Name *</Text>
        <TextInput
          style={styles.input}
          placeholder="e.g. The Old Oak Tree"
          value={name}
          onChangeText={setName}
          returnKeyType="next"
        />

        <Text style={styles.label}>Description (optional)</Text>
        <TextInput
          style={[styles.input, styles.multiline]}
          placeholder="Optional notes…"
          value={description}
          onChangeText={setDescription}
          multiline
          numberOfLines={3}
        />

        <View style={styles.row}>
          <Text style={styles.label}>Share publicly</Text>
          <Switch value={isPublic} onValueChange={setIsPublic} />
        </View>
        <Text style={styles.hint}>
          {isPublic ? 'Other runners can see and use this landmark.' : 'Only visible to you.'}
        </Text>

        <TouchableOpacity style={styles.button} onPress={handleSave} disabled={loading}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.buttonText}>Save Landmark</Text>}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  mapContainer: { flex: 1, position: 'relative' },
  map: { flex: 1 },
  mapHint: {
    position: 'absolute', bottom: 10, left: 0, right: 0,
    alignItems: 'center',
  },
  mapHintText: {
    backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
    fontSize: 12, paddingHorizontal: 12, paddingVertical: 5,
    borderRadius: 12, overflow: 'hidden',
  },
  form: { backgroundColor: '#fff', maxHeight: '50%', borderTopWidth: 1, borderTopColor: '#eee' },
  formContent: { paddingHorizontal: 20, paddingTop: 12, paddingBottom: 28 },
  label: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 4, marginTop: 12 },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 8, padding: 12, fontSize: 16 },
  multiline: { minHeight: 70, textAlignVertical: 'top' },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 },
  hint: { fontSize: 12, color: '#666', marginTop: 4 },
  coords: { color: '#888', fontSize: 12, textAlign: 'center', marginTop: 4 },
  button: { backgroundColor: '#1a73e8', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 16 },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});

